// Embed-mode signal helpers.
//
// Two independent mechanisms detect "we are running inside the partner-SSO
// iframe":
//
//   (1) Route-tree signal — `useEmbedMode()` from `layouts/EmbedLayout`.
//       True only inside the `/embed/*` subtree because EmbedLayout is the
//       only writer of that context. This is the authoritative signal for
//       ANY decision that depends on whether the tenant page is currently
//       being rendered for the iframe.
//
//   (2) JWT-claim signal — the in-memory token minted by
//       `POST /api/v1/partner/auth/token` carries `flow: "partner_sso"`.
//       Only the partner-SSO path issues that claim; partner-admin staff
//       JWTs (which carry `partner_id` but use the regular UI) do NOT.
//       Reading this is safe outside `/embed/*` because the token still
//       lives in `getEmbedToken()` until the embed tree unmounts.
//
// We need (2) for defence-in-depth in places that fire BEFORE the embed
// tree mounts or AFTER an unintended exit from `/embed/*` — e.g. a stray
// `<Link to="/">` that slips past `useTenantNavigate`. The route guard at
// `/` would otherwise see no auth (embed token is in-memory, not in
// localStorage), redirect to `/login`, and the iframe ends up showing
// NoticeAI's login page to a subscribed user.
//
// `flow === "partner_sso"` is the ONLY signal that means embed mode.
// `partner_id` alone is NOT — partner_admin staff users carry it too and
// they use the regular UI. See `BE: app/api/v1/partner/auth.py` for the
// only mint site.

import { getEmbedToken } from "../api/api";
import { decodeJwtPayload } from "../context/AuthContext";

// Decode the in-memory embed token (no signature check — JWT integrity is
// the server's job; we just need the payload). Returns null if no token,
// malformed token, or non-JWT input. Safe to call at any time.
export const getEmbedClaims = () => {
  const tok = getEmbedToken();
  if (!tok) return null;
  return decodeJwtPayload(tok);
};

// True iff the current session was bootstrapped via the partner-SSO flow.
// Stable across the in-app navigation surface (works even if the user
// has briefly navigated outside `/embed/*` — the token + claim survive
// until EmbedTokenProvider unmounts).
export const isPartnerSsoSession = () => {
  const claims = getEmbedClaims();
  return claims?.flow === "partner_sso";
};
